import ValueFormat = App.Enums.ValueFormat;
import { InjectionKey } from "vue";
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import DataTableResponseMetricDTO = App.Data.Explorer.DataTable.DataTableResponseMetricDTO;
import DataTableResponseMetricValueDTO = App.Data.Explorer.DataTable.DataTableResponseMetricValueDTO;
import DataTableResponseIndexDTO = App.Data.Explorer.DataTable.DataTableResponseIndexDTO;
import DataTableResponseRowDTO = App.Data.Explorer.DataTable.DataTableResponseRowDTO;
import DataTableResponseSubRowDTO = App.Data.Explorer.DataTable.DataTableResponseSubRowDTO;
import DataTableMetricValueType = App.Enums.DataTableMetricValueType;
import SingleMetricDO = App.Data.Explorer.SingleMetric.SingleMetricDO;
import SingleMetricPickDTO = App.Data.Explorer.SingleMetric.SingleMetricPickDTO;
import SingleMetricSumDTO = App.Data.Explorer.SingleMetric.SingleMetricSumDTO;

export const dataTableContext = Symbol() as InjectionKey<{
	openContextMenu: (
		event: MouseEvent,
		metric: DataTableMetric,
		valueIdx: number,
		valueColumn: DataTableMetricValueType,
		forColumn: boolean
	) => void;
	dataTableIndex: number;
}>;

export type processedItem = {
	horizontal: string;
	dataByOffset: [];
};

function getSortMethod(
	metricIdx: number,
	valueIdx: number,
	valueColumn: DataTableMetricValueType = "totalValue"
) {
	return (function (metricIdx, valueIdx, valueColumn) {
		return function (a: MetricsDataTableRow, b: MetricsDataTableRow) {
			const av = a.subRow.metrics[metricIdx].values[valueIdx][valueColumn];
			const bv = b.subRow.metrics[metricIdx].values[valueIdx][valueColumn];

			if (av < bv) {
				return -1;
			}
			if (av > bv) {
				return 1;
			}

			return 0;
		};
	})(metricIdx, valueIdx, valueColumn);
}

export class DataTableMetric implements DataTableResponseMetricDTO {
	constructor(
		public name: string,
		public format: ValueFormat,
		public values: DataTableResponseMetricValueDTO[]
	) {}

	get hasComparison() {
		return this.values.length > 1;
	}
}

export type MetricsDataTableRow = {
	row: DataTableResponseRowDTO;
	subRowIdx: number;
	subRow: DataTableResponseSubRowDTO;
};

export class DataTableFormatter {
	public isEmpty: boolean;
	private metrics: DataTableResponseMetricDTO[];

	constructor(private dataTable: DataTableResponseDTO) {
		this.isEmpty =
			this.dataTable.rows.length === 0 ||
			this.dataTable.rows[0].subRows.length === 0;

		this.metrics = this.isEmpty
			? []
			: this.dataTable.rows[0].subRows[0].metrics;
	}

	toHeaders(): any[] {
		if (this.isEmpty) {
			return [];
		}

		const headers = [this.getHorizontalHeader()];

		const offsetHeader = this.getOffsetHeader();
		if (offsetHeader) {
			headers.push(offsetHeader);
		}

		return [...headers, ...this.getMetricHeaders()];
	}

	private getHorizontalHeader() {
		return {
			title: `properties.${this.dataTable.dataTableDO.dimensions[0].name_clean}.title`,
			key: "horizontal",
			align: "start",
			sortable: true,
		};
	}

	private getOffsetHeader() {
		if (this.dataTable.dataTableDO.dimensions.length <= 1) {
			return null;
		}

		return {
			title: `properties.${this.dataTable.dataTableDO.dimensions[1].name_clean}.title`,
			key: "offset",
			align: "start",
			sortable: false,
		};
	}

	private getMetricHeaders(): any[] {
		return this.metrics.map((metric, metricIdx) =>
			this.createMetricHeader(metric, metricIdx)
		);
	}

	private createMetricHeader(
		metric: DataTableResponseMetricDTO,
		metricIdx: number
	): any {
		const header = this.createBaseHeader(metric, metricIdx);

		if (this.shouldUseSimpleHeader(metric)) {
			return header;
		}

		header.align = "center";
		header.sortable = false;
		header.children = this.createComparisonSubHeaders(metric, metricIdx);

		// This is specifically for when split prediction is enabled without a comparison.
		// The value columns are normally under the comparison column. If there is no comparison this leads to a useless column
		// with the title Y (current year). This removes that and replaces the children with the value columns.
		if (header.children.length === 1 && header.children[0].title === "Y") {
			header.children = header.children[0].children;
		}

		return header;
	}

	private createBaseHeader(
		metric: DataTableResponseMetricDTO,
		metricIdx: number
	): any {
		return {
			title: `properties.${metric.name}.title`,
			key: `${metric.name}:totalValue:0`,
			align: "end",
			sortable: true,
			children: undefined,
			sortRaw: getSortMethod(metricIdx, 0),
			metric,
		};
	}

	private shouldUseSimpleHeader(metric: DataTableResponseMetricDTO): boolean {
		return (
			(this.dataTable.dataTableDO.is_comparison_nested ||
				metric.values.length === 1) &&
			!this.dataTable.dataTableDO.split_prediction
		);
	}

	private createComparisonSubHeaders(
		metric: DataTableResponseMetricDTO,
		metricIdx: number
	): any[] {
		if (this.dataTable.dataTableDO.is_comparison_nested) {
			return [
				this.createComparisonSubHeader(metric, metricIdx, metric.values[0], 0),
			];
		}

		return metric.values.map((value, valueIdx) =>
			this.createComparisonSubHeader(metric, metricIdx, value, valueIdx)
		);
	}

	private createComparisonSubHeader(
		metric: DataTableResponseMetricDTO,
		metricIdx: number,
		value: any,
		valueIdx: number
	): any {
		const subHeader = this.createBaseComparisonHeader(
			metric,
			metricIdx,
			value,
			valueIdx
		);

		if (this.shouldCreateValueColumns(value)) {
			subHeader.align = "center";
			subHeader.sortable = false;
			subHeader.children = this.createExpandedValueColumns(
				metric,
				metricIdx,
				value,
				valueIdx
			);
		}

		return subHeader;
	}

	private createBaseComparisonHeader(
		metric: DataTableResponseMetricDTO,
		metricIdx: number,
		value: any,
		valueIdx: number
	): any {
		const headerProps = { class: `metrics-data-table--color-${valueIdx + 1}` };
		return {
			title: value.comparison,
			key: `${metric.name}:totalValue:${valueIdx}`,
			align: "center",
			sortable: true,
			children: undefined,
			headerProps,
			sortRaw: getSortMethod(metricIdx, valueIdx),
			metric,
		};
	}

	private shouldCreateValueColumns(value: any): boolean {
		return (
			this.dataTable.dataTableDO.show_change_absolute ||
			this.dataTable.dataTableDO.show_change_relative_to_current ||
			(this.dataTable.dataTableDO.split_prediction && value.comparison === "Y")
		);
	}

	private createExpandedValueColumns(
		metric: DataTableResponseMetricDTO,
		metricIdx: number,
		value: any,
		valueIdx: number
	): any[] {
		const children = [];
		const showPrediction =
			this.dataTable.dataTableDO.split_prediction && value.comparison === "Y";
		const currentYearValueColumn = showPrediction
			? "actualValue"
			: "totalValue";

		children.push(
			this.createValueColumn(
				metric,
				metricIdx,
				valueIdx,
				currentYearValueColumn
			)
		);

		if (showPrediction) {
			children.push(
				this.createPredictedValueColumn(metric, metricIdx, valueIdx)
			);
		}

		if (value.comparison !== "Y") {
			if (this.dataTable.dataTableDO.show_change_absolute) {
				children.push(
					this.createDeltaAbsoluteColumn(metric, metricIdx, valueIdx)
				);
			}
			if (this.dataTable.dataTableDO.show_change_relative_to_current) {
				children.push(
					this.createDeltaRelativeColumn(metric, metricIdx, valueIdx)
				);
			}
		}

		return children;
	}

	private createValueColumn(
		metric: DataTableResponseMetricDTO,
		metricIdx: number,
		valueIdx: number,
		columnType: DataTableMetricValueType
	): any {
		return {
			title: "V",
			key: `${metric.name}:${columnType}:${valueIdx}`,
			align: "center",
			sortable: true,
			headerProps: { class: `metrics-data-table--color-${valueIdx + 1}` },
			sortRaw: getSortMethod(metricIdx, valueIdx, columnType),
			metric,
		};
	}

	private createPredictedValueColumn(
		metric: DataTableResponseMetricDTO,
		metricIdx: number,
		valueIdx: number
	): any {
		return {
			title: "V (P) ",
			key: `${metric.name}:predictedValue:${valueIdx}`,
			align: "center",
			sortable: true,
			headerProps: { class: `metrics-data-table--color-${valueIdx + 1}` },
			sortRaw: getSortMethod(metricIdx, valueIdx, "predictedValue"),
			metric,
		};
	}

	private createDeltaAbsoluteColumn(
		metric: DataTableResponseMetricDTO,
		metricIdx: number,
		valueIdx: number
	): any {
		return {
			title: "ΔAbs",
			key: `${metric.name}:deltaAbsolute:${valueIdx}`,
			align: "center",
			sortable: true,
			headerProps: { class: `metrics-data-table--color-${valueIdx + 1}` },
			sortRaw: getSortMethod(metricIdx, valueIdx, "deltaAbsolute"),
			metric,
		};
	}

	private createDeltaRelativeColumn(
		metric: DataTableResponseMetricDTO,
		metricIdx: number,
		valueIdx: number
	): any {
		return {
			title: "ΔRel",
			key: `${metric.name}:deltaRelative:${valueIdx}`,
			align: "center",
			sortable: true,
			headerProps: { class: `metrics-data-table--color-${valueIdx + 1}` },
			sortRaw: getSortMethod(metricIdx, valueIdx, "deltaRelative"),
			metric,
		};
	}
}

export const isPickMetric = (
	metric: SingleMetricDO
): metric is SingleMetricPickDTO => {
	return metric?.type === "pick";
};

export const isColumnMetric = (
	metric: SingleMetricDO
): metric is SingleMetricSumDTO => {
	return metric?.type !== "pick";
};
